import { AlertsCreatedCard } from '@/widgets/alerts-created-card';
import { AlertsPerResidentCard } from '@/widgets/alerts-per-resident-card';
import { NumberOfResidentsCard } from '@/widgets/facilities-average-residents-card';
import { FacilityOccupancyForecastCard } from '@/widgets/facilities-occupancy-forecast-cards';
import { FacilityPerformanceKpiCard } from '@/widgets/facility-performance-kpi-card';
import { OccupancyLosCard } from '@/widgets/occupancy-los-card';
import { OverallOccupancyCard } from '@/widgets/overall-occupancy-card';
import { PayerDistribution } from '@/widgets/payer-distribution';
import { StaffingRatioCard } from '@/widgets/staffing-ratio-cards';
import { TotalStaffCard } from '@/widgets/total-staff-card';
import type { Dayjs } from 'dayjs';
import type { FC } from 'react';

type SingleFacilityGeneralTabProps = {
  facilityId: string;
  facilityName: string;
  dateRange?: {
    from: Dayjs;
    to: Dayjs;
  };
};

export const SingleFacilityGeneralTab: FC<SingleFacilityGeneralTabProps> = ({
  facilityId,
  facilityName,
  dateRange,
}) => {
  if (!dateRange) {
    return null;
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Row 1: Occupancy donut | Payer distribution | KPI summary cards.
          Mirrors the main Facilities page Row 1 structure exactly so the donut
          cards and the 2x2 summary grid stay aligned. */}
      <div className="flex h-[236px] gap-4">
        <div className="flex gap-4">
          <OverallOccupancyCard dateRange={dateRange} />
          <PayerDistribution dateRange={dateRange} />
        </div>
        {/* flex-1 min-w-0, not w-full — see the note in facilities.page.tsx. */}
        <div className="flex min-w-0 flex-1 flex-col gap-4">
          <div className="flex w-full flex-1 gap-4">
            <AlertsCreatedCard dateRange={dateRange} />
            <TotalStaffCard title="Number of Staff" dateRange={dateRange} />
          </div>
          <div className="flex w-full flex-1 gap-4">
            <AlertsPerResidentCard dateRange={dateRange} />
            <StaffingRatioCard dateRange={dateRange} />
          </div>
        </div>
      </div>

      {/* Row 2: Number of Residents + Facility Performance | Occupancy vs LOS scatter.
          Left column width (416px) matches Row 1's donut group (2x w-[200px] + gap-4)
          so the column seam lines up vertically between the two rows. The residents
          card is a short KPI card, so it sizes to content and the performance card
          takes the rest of the 380px the scatter occupies. */}
      <div className="flex gap-4">
        <div className="flex h-[380px] w-[416px] shrink-0 flex-col gap-4">
          <NumberOfResidentsCard dateRange={dateRange} />
          <div className="min-h-0 flex-1">
            <FacilityPerformanceKpiCard
              dateRange={dateRange}
              facilityId={+facilityId}
            />
          </div>
        </div>
        {/* min-w-0, not just flex-1: same trap noted in facilities.page.tsx —
            without it a flex item won't shrink below its content's intrinsic
            width, so the scatter chart held its pre-panel width and slid
            under the AI Insight panel instead of narrowing with the column
            (Anna, ST #182 comment #427). */}
        <div className="min-w-0 flex-1">
          <OccupancyLosCard
            includeAllOrgFacilities
            heightClassName="h-[380px]"
            chartHeightClassName="h-[280px]"
            dateRange={dateRange}
            highlightFacilityName={facilityName}
          />
        </div>
      </div>

      {/* Row 3: Occupancy Actual + Forecasted, full width. The revenue forecast moved
          to the Financial tab (ST #137). */}
      <div className="h-[350px]">
        <FacilityOccupancyForecastCard facilityId={facilityId} />
      </div>
    </div>
  );
};
